import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Button, FormControl, FormHelperText, FormLabel, Paper, Stack, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import type { FieldComponentProps, FieldTypeSpec } from '../types';
import { matchesAccept } from './FileField';

export interface ImageConfig {
  accept?: string;
  /** Limit on the encoded data URL, which is what gets saved. */
  maxEncodedKb?: number;
  previewHeight?: number;
  emptyText?: string;
  uploadLabel?: string;
}

const IMAGE_TYPES = 'image/png,image/svg+xml,image/jpeg,image/webp';

function readDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** An image saved inline as a data URL (logos and the like): preview, replace, remove. */
export function ImageInput({ field, value, onChange, error }: FieldComponentProps<string | null, ImageConfig>) {
  const {
    accept = IMAGE_TYPES, maxEncodedKb, previewHeight = 140, emptyText = 'No image', uploadLabel = 'Upload',
  } = field.config;
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string>();
  const editable = !field.disabled && !field.readOnly;

  const pick = (file: File | undefined) => {
    if (!file) return;
    if (!matchesAccept(file, accept)) {
      setProblem(`${file.name} is not one of the accepted images.`);
      return;
    }
    readDataUrl(file)
      .then((dataUrl) => {
        // base64 is ~1.37x the raw bytes, so a raw-size check would pass images the save then refuses.
        if (maxEncodedKb && dataUrl.length > maxEncodedKb * 1024) {
          setProblem(`${file.name} is too large once encoded (${Math.round(dataUrl.length / 1024)} KB; `
            + `the limit is ${maxEncodedKb} KB). Pick a smaller image.`);
          return;
        }
        setProblem(undefined);
        onChange(dataUrl);
      })
      .catch(() => setProblem(`${file.name} could not be read.`));
  };

  const message = problem ?? error ?? field.helperText;
  return (
    <FormControl fullWidth={field.fullWidth} error={!!(problem ?? error)} className={field.className} sx={field.sx}>
      {field.label && <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>{field.label}</FormLabel>}
      <Paper variant="outlined" sx={{
        height: previewHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50', mb: 1,
      }}>
        {value
          ? <Box component="img" src={value} alt="" sx={{ maxHeight: '80%', maxWidth: '85%' }} />
          : <Typography variant="caption" color="text.disabled">{emptyText}</Typography>}
      </Paper>
      {editable && (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<UploadIcon />} onClick={() => input.current?.click()}
                  {...field.props}>
            {uploadLabel}
          </Button>
          {value && (
            <Button size="small" variant="text" color="error" startIcon={<DeleteIcon />} onClick={() => onChange(null)}>
              Remove
            </Button>
          )}
          <input ref={input} type="file" hidden accept={accept}
                 onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
        </Stack>
      )}
      {message && <FormHelperText sx={{ mx: 0 }}>{message}</FormHelperText>}
    </FormControl>
  );
}

export const imageTypes: Record<string, FieldTypeSpec> = {
  image: {
    component: ImageInput,
    display: (value) => <Box component="img" src={value as string} alt="" sx={{ maxHeight: 32, maxWidth: 120 }} />,
  },
};
